import { cloneElement, isValidElement, useId, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { useLocale } from '@/lib/i18n';

interface AuthForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  licenseNumber: string;
}

interface PhoneChallenge {
  challengeId: string;
  phoneMask?: string;
  demoCode?: string;
}

const inputClass = 'mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/25';

export function CustomerAuthPanel({
  ready,
  authMode,
  recovery,
  authForm,
  challenge,
  code,
  remember,
  authError,
  authPending,
  notice,
  emailDemo,
  resetCode,
  resetPassword,
  resetConfirm,
  onAuthMode,
  onRecovery,
  onAuthForm,
  onCode,
  onRemember,
  onResetCode,
  onResetPassword,
  onResetConfirm,
  onSubmitAuth,
  onRequestReset,
  onSubmitReset,
  onVerifyCode,
  onResendCode,
}: {
  ready: boolean;
  authMode: 'login' | 'register';
  recovery: 'login' | 'forgot' | 'reset';
  authForm: AuthForm;
  challenge: PhoneChallenge | null;
  code: string;
  remember: boolean;
  authError: string | null;
  authPending: boolean;
  notice: string | null;
  emailDemo: string | null;
  resetCode: string;
  resetPassword: string;
  resetConfirm: string;
  onAuthMode: (mode: 'login' | 'register') => void;
  onRecovery: (mode: 'login' | 'forgot' | 'reset') => void;
  onAuthForm: (form: AuthForm) => void;
  onCode: (value: string) => void;
  onRemember: (value: boolean) => void;
  onResetCode: (value: string) => void;
  onResetPassword: (value: string) => void;
  onResetConfirm: (value: string) => void;
  onSubmitAuth: (event: FormEvent) => void;
  onRequestReset: (event: FormEvent) => void;
  onSubmitReset: (event: FormEvent) => void;
  onVerifyCode: (event: FormEvent) => void;
  onResendCode: () => void;
}) {
  const { t } = useLocale();

  if (!ready) {
    return (
      <div className="min-h-[70vh] bg-slate-50 px-4 py-10">
        <div className="mx-auto h-96 max-w-5xl animate-pulse rounded-3xl bg-slate-200" />
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-slate-50 px-4 py-8 sm:py-12">
      <div className="mx-auto grid max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <aside className="order-2 bg-[#12151a] px-6 py-8 text-white sm:px-8 sm:py-10 lg:order-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-[var(--color-primary)]">{t('prof.authEyebrow')}</span>
            <span className="rounded-full bg-white/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-300">{t('prof.authBriefing')}</span>
          </div>
          <div className="relative mt-5 overflow-hidden rounded-3xl">
            <img
              src="/login/login-renter.jpg"
              alt={t('prof.authImage')}
              className="h-48 w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12151a] via-[#12151a]/25 to-transparent" />
          </div>
          <h1 className="mt-5 text-3xl font-semibold tracking-tight">{t('prof.authTitle')}</h1>
          <p className="mt-3 text-sm leading-6 text-slate-300">{t('prof.authLead')}</p>
          <ul className="mt-8 space-y-3 text-sm leading-5 text-slate-200">
            <li className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">{t('prof.authPointBookings')}</li>
            <li className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">{t('prof.authPointPhone')}</li>
          </ul>
          <a href="/login" className="mt-8 inline-flex text-sm font-medium text-slate-300 underline-offset-4 hover:text-white hover:underline">
            {t('prof.authStaff')}
          </a>
        </aside>

        <section className="order-1 px-6 py-8 sm:px-8 sm:py-10 lg:order-2">
          {challenge ? (
            <form onSubmit={onVerifyCode} className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-950">{t('prof.verify')}</h2>
              <p className="text-sm leading-6 text-slate-500">{t('prof.codeHint')} {challenge.phoneMask ?? ''}</p>
              {challenge.demoCode ? <p className="text-sm font-semibold text-amber-700">{t('prof.demoCode')}: {challenge.demoCode}</p> : null}
              <Field label={t('prof.code')}>
                <input
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => onCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputClass} text-center text-lg tracking-[0.4em]`}
                />
              </Field>
              <Alert message={authError} />
              <div className="flex flex-wrap items-center gap-4">
                <button type="submit" disabled={authPending || code.length !== 6} className="min-h-11 rounded-xl bg-[var(--color-primary)] px-5 text-sm font-semibold text-slate-950 disabled:opacity-60">
                  {t('prof.verify')}
                </button>
                <button type="button" disabled={authPending} onClick={onResendCode} className="text-sm font-semibold text-slate-600 hover:text-slate-950 disabled:opacity-60">
                  {t('prof.resend')}
                </button>
              </div>
            </form>
          ) : authMode === 'login' && recovery !== 'login' ? (
            <form onSubmit={recovery === 'forgot' ? onRequestReset : onSubmitReset} className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-950">{t('prof.forgot')}</h2>
              <p className="text-sm leading-6 text-slate-500">{t('prof.emailCodeSent')}</p>
              <Field label={t('prof.email')}>
                <input required type="email" autoComplete="email" value={authForm.email} onChange={(event) => onAuthForm({ ...authForm, email: event.target.value })} className={inputClass} />
              </Field>
              {recovery === 'reset' ? (
                <>
                  {emailDemo ? <p className="text-sm font-semibold text-amber-700">{t('prof.demoCode')}: {emailDemo}</p> : null}
                  <Field label={t('prof.emailCode')}>
                    <input required inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={resetCode} onChange={(event) => onResetCode(event.target.value.replace(/\D/g, '').slice(0, 6))} className={`${inputClass} text-center tracking-[0.4em]`} />
                  </Field>
                  <SecretField label={t('prof.newPassword')} autoComplete="new-password" minLength={12} value={resetPassword} onChange={onResetPassword} showLabel={t('prof.showPassword')} hideLabel={t('prof.hidePassword')} />
                  <SecretField label={t('prof.confirmPassword')} autoComplete="new-password" minLength={12} value={resetConfirm} onChange={onResetConfirm} showLabel={t('prof.showPassword')} hideLabel={t('prof.hidePassword')} />
                  {resetPassword !== '' && resetConfirm !== '' && resetPassword !== resetConfirm ? (
                    <p className="text-sm font-medium text-rose-600">{t('prof.passwordMismatch')}</p>
                  ) : null}
                </>
              ) : null}
              <Alert message={authError} />
              <div className="flex flex-wrap items-center gap-4">
                <button type="submit" disabled={authPending} className="min-h-11 rounded-xl bg-[var(--color-primary)] px-5 text-sm font-semibold text-slate-950 disabled:opacity-60">
                  {recovery === 'forgot' ? t('prof.sendCode') : t('prof.resetPassword')}
                </button>
                <button type="button" onClick={() => { onRecovery('login'); }} className="text-sm font-semibold text-slate-600 hover:text-slate-950">
                  {t('prof.signIn')}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={onSubmitAuth} className="space-y-4">
              <div className="flex gap-2 rounded-2xl bg-slate-100 p-1">
                <ModeButton active={authMode === 'login'} onClick={() => { onAuthMode('login'); onRecovery('login'); }}>{t('prof.signIn')}</ModeButton>
                <ModeButton active={authMode === 'register'} onClick={() => { onAuthMode('register'); onRecovery('login'); }}>{t('prof.register')}</ModeButton>
              </div>
              <p className="text-sm leading-6 text-slate-500">{t('prof.accountHint')}</p>
              {authMode === 'register' ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('prof.firstName')}>
                    <input required autoComplete="given-name" value={authForm.firstName} onChange={(event) => onAuthForm({ ...authForm, firstName: event.target.value })} className={inputClass} />
                  </Field>
                  <Field label={t('prof.lastName')}>
                    <input required autoComplete="family-name" value={authForm.lastName} onChange={(event) => onAuthForm({ ...authForm, lastName: event.target.value })} className={inputClass} />
                  </Field>
                </div>
              ) : null}
              <Field label={t('prof.email')}>
                <input required type="email" autoComplete="email" value={authForm.email} onChange={(event) => onAuthForm({ ...authForm, email: event.target.value })} className={inputClass} />
              </Field>
              <SecretField
                label={t('prof.password')}
                autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                minLength={authMode === 'register' ? 12 : undefined}
                value={authForm.password}
                onChange={(value) => onAuthForm({ ...authForm, password: value })}
                showLabel={t('prof.showPassword')}
                hideLabel={t('prof.hidePassword')}
              />
              {authMode === 'register' ? (
                <Field label={t('prof.phone')}>
                  <input required autoComplete="tel" value={authForm.phone} onChange={(event) => onAuthForm({ ...authForm, phone: event.target.value })} className={inputClass} />
                </Field>
              ) : null}
              {notice ? <p role="status" className="text-sm font-medium text-emerald-700">{notice}</p> : null}
              <Alert message={authError} />
              <div className="flex flex-wrap items-center gap-4">
                <button type="submit" disabled={authPending} className="min-h-11 rounded-xl bg-[var(--color-primary)] px-5 text-sm font-semibold text-slate-950 disabled:opacity-60">
                  {authPending ? (authMode === 'register' ? t('prof.authCreating') : t('prof.authWorking')) : (authMode === 'register' ? t('prof.register') : t('prof.signIn'))}
                </button>
                {authMode === 'login' ? (
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={remember} onChange={(event) => onRemember(event.target.checked)} className="size-4 accent-[var(--color-primary)]" />
                    {t('prof.remember')}
                  </label>
                ) : null}
              </div>
              {authMode === 'login' ? (
                <button type="button" onClick={() => onRecovery('forgot')} className="text-sm font-medium text-slate-600 hover:text-slate-950">
                  {t('prof.forgot')}
                </button>
              ) : null}
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-10 flex-1 rounded-xl px-3 text-sm font-semibold ${active ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: ReactElement<{ id?: string }> }) {
  const id = useId();
  return (
    <label htmlFor={id} className="block text-xs font-medium text-slate-600">
      {label}
      {isValidElement(children) ? cloneElement(children, { id }) : children}
    </label>
  );
}

function SecretField({
  label,
  autoComplete,
  minLength,
  value,
  onChange,
  showLabel,
  hideLabel,
}: {
  label: string;
  autoComplete: string;
  minLength?: number;
  value: string;
  onChange: (value: string) => void;
  showLabel: string;
  hideLabel: string;
}) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-600">{label}</label>
      <span className="relative mt-1.5 block">
        <input
          id={id}
          required
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          minLength={minLength}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`${inputClass} mt-0 pr-12`}
        />
        <button
          type="button"
          aria-pressed={visible}
          aria-label={visible ? hideLabel : showLabel}
          onClick={() => setVisible((current) => !current)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-500 hover:text-slate-900"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </span>
    </div>
  );
}

function Alert({ message }: { message: string | null }) {
  if (!message) return null;
  return <p role="alert" className="text-sm font-medium text-rose-600">{message}</p>;
}
